import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { basicURL } from "../../apiManager/APIConfig";
import appIcon from "../../assets/images/logos/mini_logo.png";

const FALLBACK_LINKS = {
  ios: "https://apps.apple.com/app/microlab/id6469486411",
  android: "https://play.google.com/store/apps/details?id=com.saeedtkh.microlab",
};

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  const iPadOs =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  if (/iPad|iPhone|iPod/i.test(ua) || iPadOs) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

function appleAppId(url) {
  const match = String(url || "").match(/id(\d+)/);
  return match ? match[1] : "6469486411";
}

function androidPackage(url) {
  const match = String(url || "").match(/[?&]id=([^&#]+)/);
  return match ? decodeURIComponent(match[1]) : "com.saeedtkh.microlab";
}

function appStoreHref(platform, httpsUrl) {
  if (platform === "ios") {
    return `itms-apps://apps.apple.com/app/id${appleAppId(httpsUrl)}`;
  }
  return httpsUrl;
}

function playStoreHref(platform, httpsUrl) {
  if (platform === "android") {
    return `market://details?id=${androidPackage(httpsUrl)}`;
  }
  return httpsUrl;
}

export default function DownloadAppPage() {
  const platform = useMemo(() => detectPlatform(), []);
  const [links, setLinks] = useState(FALLBACK_LINKS);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await fetch(`${basicURL}public/public`, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data?.data) ? data.data : [];
        const value = (key) => {
          const raw = list.find((row) => row?.key === key)?.data;
          return typeof raw === "string" ? raw.trim() : "";
        };
        if (!mounted) return;
        setLinks({
          ios: value("appstore_link") || FALLBACK_LINKS.ios,
          android: value("googleplay_link") || FALLBACK_LINKS.android,
        });
      } catch {
        /* fallbacks stay in place */
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        px: 2.5,
        py: 4,
        bgcolor: "#f4f5f7",
        color: "#111318",
      }}
    >
      <Stack
        spacing={2.5}
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 400,
          textAlign: "center",
        }}
      >
        <Box
          component="img"
          src={appIcon}
          alt="Microlab"
          sx={{
            width: 96,
            height: 96,
            objectFit: "contain",
            filter: "drop-shadow(0 12px 24px rgba(17,19,24,0.16))",
          }}
        />
        <Stack spacing={1} alignItems="center">
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 32, sm: 36 },
              fontWeight: 650,
              letterSpacing: "-0.04em",
              lineHeight: 1.05,
            }}
          >
            Microlab
          </Typography>
          <Typography
            sx={{
              fontSize: 16,
              lineHeight: 1.45,
              color: "#5d6570",
              maxWidth: 320,
            }}
          >
            Histology, anatomy, and embryology on your phone.
          </Typography>
        </Stack>

        <Stack spacing={1.25} sx={{ width: "100%", pt: 1 }}>
          <Button
            fullWidth
            component="a"
            href={appStoreHref(platform, links.ios)}
            sx={buttonSx}
          >
            Download on the App Store
          </Button>
          <Button
            fullWidth
            component="a"
            href={playStoreHref(platform, links.android)}
            variant="outlined"
            sx={{
              ...buttonSx,
              bgcolor: "#fff",
              color: "#111318",
              border: "1px solid #e4e6ea",
              "&.MuiButton-outlined": {
                border: "1px solid #e4e6ea",
                color: "#111318",
              },
              "&:hover, &.MuiButton-outlined:hover": {
                bgcolor: "#f7f8fa",
                border: "1px solid #e4e6ea",
                boxShadow: "none",
              },
            }}
          >
            Get it on Google Play
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

const buttonSx = {
  height: 52,
  borderRadius: "14px",
  bgcolor: "#111318",
  color: "#fff",
  textTransform: "none",
  fontSize: 16,
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { bgcolor: "#2a2e36", boxShadow: "none" },
};
